import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { useCategories, useMasterData } from '../masterdata/queries';
import { flattenCategories, STATE_CATEGORIES } from '../tickets/presentation';
import type { MacroAction, MacroActionType } from './api';
import { useCreateMacro, useDeleteMacro, useMacros, useUpdateMacro } from './queries';
import { Select } from '../../components/Select';

/**
 * Verwaltung der Makros -- F1-38, F1-39, K-20, K-65.
 *
 * Die Aktionen werden ueber Auswahlfelder zusammengestellt und nicht als Text
 * eingegeben. Das ist keine Bequemlichkeit, sondern die Umsetzung der Regel
 * aus docs/03, Abschnitt 8: Ein Makro ist eine Einstellung, kein Programm.
 * Ein Eingabefeld fuer freie Ausdruecke waere genau das Gegenteil -- und die
 * Rechtepruefung im System waere fortan eine Frage der Auslegung dieser
 * Ausdruecke.
 *
 * `set_state` waehlt eine **Zustandsgruppe**, keinen einzelnen Zustand: Ein
 * Makro soll in jedem Mandanten laufen, und die Zustandsnamen unterscheiden
 * sich je Mandant.
 */
const FIELD_ACTIONS: readonly MacroActionType[] = [
  'set_queue',
  'set_type',
  'set_priority',
  'set_category',
];

const TEXT_ACTIONS: readonly MacroActionType[] = ['add_note', 'add_reply'];

export function MacrosPage() {
  const { t } = useTranslation();
  const macros = useMacros();
  const create = useCreateMacro();
  const update = useUpdateMacro();
  const remove = useDeleteMacro();

  const queues = useMasterData('queue');
  const types = useMasterData('type');
  const priorities = useMasterData('priority');
  const categories = useCategories();
  const tags = useMasterData('tag');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [actions, setActions] = useState<MacroAction[]>([]);
  const [draftType, setDraftType] = useState<MacroActionType>('add_note');
  const [draftValue, setDraftValue] = useState('');

  const optionsFor = (type: MacroActionType): { id: string; label: string }[] => {
    switch (type) {
      case 'set_queue':
        return (queues.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }));
      case 'set_type':
        return (types.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }));
      case 'set_priority':
        return (priorities.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }));
      case 'set_category':
        return flattenCategories(categories.data ?? []);
      case 'add_tag':
      case 'remove_tag':
        return (tags.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }));
      case 'set_state':
        return STATE_CATEGORIES.map((category) => ({
          id: category,
          label: t(`stateCategory.${category}`),
        }));
      default:
        return [];
    }
  };

  const needsText = TEXT_ACTIONS.includes(draftType);
  const options = optionsFor(draftType);

  return (
    <>
      <PageHeader title={t('macros.title')} subtitle={t('macros.subtitle')} />

      <section className="card">
        <h2>{t('macros.new')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="macro-name">
            {t('masterdata.name')}
          </label>
          <input
            id="macro-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="macro-description">
            {t('templates.description')}
          </label>
          <input
            id="macro-description"
            className="field__control"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>

        <h3>{t('macros.actions')}</h3>

        {actions.length === 0 ? (
          <p className="table__muted">{t('macros.noActions')}</p>
        ) : (
          <ol className="task-list">
            {actions.map((action, index) => (
              <li key={`${action.type}-${String(index)}`} className="task-list__item">
                <Badge tone="neutral">{t(`macroAction.${action.type}`)}</Badge>
                <span>{labelForValue(action, optionsFor(action.type))}</span>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setActions((current) => current.filter((_, position) => position !== index));
                  }}
                >
                  {t('masterdata.delete')}
                </Button>
              </li>
            ))}
          </ol>
        )}

        <div className="row">
          <Select
            className="field__control"
            aria-label={t('macros.actionType')}
            value={draftType}
            onChange={(event) => {
              setDraftType(event.target.value as MacroActionType);
              setDraftValue('');
            }}
          >
            {(
              [
                ...FIELD_ACTIONS,
                'set_state',
                'add_tag',
                'remove_tag',
                ...TEXT_ACTIONS,
              ] as MacroActionType[]
            ).map((type) => (
              <option key={type} value={type}>
                {t(`macroAction.${type}`)}
              </option>
            ))}
          </Select>

          {needsText ? (
            <input
              className="field__control"
              aria-label={t('macros.actionText')}
              placeholder={t('macros.actionText')}
              value={draftValue}
              onChange={(event) => {
                setDraftValue(event.target.value);
              }}
            />
          ) : (
            <Select
              className="field__control"
              aria-label={t('macros.actionValue')}
              value={draftValue}
              onChange={(event) => {
                setDraftValue(event.target.value);
              }}
            >
              <option value="">{t('macros.chooseValue')}</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}

          <Button
            disabled={!draftValue.trim()}
            onClick={() => {
              setActions((current) => [...current, { type: draftType, value: draftValue }]);
              setDraftValue('');
            }}
          >
            {t('macros.addAction')}
          </Button>
        </div>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!name.trim() || actions.length === 0}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              { name: name.trim(), description, actions, is_active: true, sort_order: 0 },
              {
                onSuccess: () => {
                  setName('');
                  setDescription('');
                  setActions([]);
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>

      <section className="card card--flush">
        {macros.isPending ? <SkeletonTable columns={4} /> : null}
        {macros.isError ? <ErrorNotice error={macros.error} /> : null}

        {macros.data?.length === 0 ? (
          <EmptyState icon="macros" title={t('macros.empty')} hint={t('macros.emptyHint')} />
        ) : null}

        {macros.data && macros.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('macros.actions')}</th>
                  <th scope="col">{t('masterdata.isActive')}</th>
                  <th scope="col">{t('tickets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {macros.data.map((macro) => (
                  <tr key={macro.id}>
                    <td>{macro.name}</td>
                    <td className="table__muted">
                      {macro.actions.map((action) => t(`macroAction.${action.type}`)).join(', ')}
                    </td>
                    <td>
                      <Badge tone={macro.is_active ? 'success' : 'neutral'}>
                        {macro.is_active ? t('masterdata.yes') : t('masterdata.no')}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          update.mutate({
                            macroId: macro.id,
                            payload: { is_active: !macro.is_active },
                          });
                        }}
                      >
                        {macro.is_active ? t('snippets.deactivate') : t('snippets.activate')}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          remove.mutate(macro.id);
                        }}
                      >
                        {t('masterdata.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}

/** Zeigt den Wert einer Aktion lesbar an -- Kennungen sagen niemandem etwas. */
function labelForValue(action: MacroAction, options: { id: string; label: string }[]): string {
  if (!action.value) return '';
  return options.find((option) => option.id === action.value)?.label ?? action.value;
}
